import { SignedIn, SignedOut } from "@clerk/clerk-react";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  BadgeCheck,
  BookOpen,
  Brain,
  ChevronDown,
  Code,
  GraduationCap,
  Languages,
  School,
  ShieldCheck,
  Sparkles,
  Stethoscope,
  TrendingUp,
} from "lucide-react";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";

import AnimatedNumber from "@/components/AnimatedNumber";
import Header from "@/components/Header";
import HeroIllustration from "@/components/HeroIllustration";
import JsonLd from "@/components/JsonLd";
import PageMeta from "@/components/PageMeta";
import SiteFooter from "@/components/SiteFooter";
import TopicPicker from "@/components/TopicPicker";
import { Button } from "@/components/ui/button";
import { topicCategories } from "@/content/topics";
import { trackEvent } from "@/lib/analytics";
import { startAnonymousTrial } from "@/lib/demo";
import { homeGraph } from "@/lib/seo";

const FlexoCarousel = lazy(() => import("@/components/FlexoCarousel"));
const PricingSection = lazy(() => import("@/components/PricingSection"));
const nursingTopics = topicCategories.find((category) => category.id === "nursing").topics;

const clerkPubKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;
const isClerkConfigured = clerkPubKey && !clerkPubKey.includes("YOUR_CLERK");

const MotionLink = motion.create(Link);

const SAMPLE_NCLEX_ID = "sample-nclex";
const SAMPLE_SAT_ID = "sample-sat";

export default function LandingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();
  const [demoBookLimit, setDemoBookLimit] = useState(1);
  const [showCarousel, setShowCarousel] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [statsInView, setStatsInView] = useState(false);
  const carouselRef = useRef(null);
  const pricingRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const loadPublicFeatureFlags = async () => {
      try {
        const { api } = await import("@/lib/api");
        const response = await api.getPublicFeatureFlags();
        if (isMounted) {
          setDemoBookLimit(response.data.demo_book_limit ?? 1);
        }
      } catch {
        // Keep the local fallback when the backend config endpoint is unavailable.
      }
    };

    loadPublicFeatureFlags();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const target = document.getElementById(id);
    if (!target) return;
    requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [location.hash]);

  useEffect(() => {
    if (showCarousel && showPricing) return;

    if (typeof IntersectionObserver === "undefined") {
      setShowCarousel(true);
      setShowPricing(true);
      return;
    }

    const observers = [];
    const observe = (target, onReveal) => {
      if (!target) return;

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            onReveal();
            observer.disconnect();
          }
        },
        { rootMargin: "320px 0px" },
      );

      observer.observe(target);
      observers.push(observer);
    };

    if (!showCarousel) {
      observe(carouselRef.current, () => setShowCarousel(true));
    }

    if (!showPricing) {
      observe(pricingRef.current, () => setShowPricing(true));
    }

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, [showCarousel, showPricing]);

  const launchTrial = () => {
    startAnonymousTrial();
    navigate("/dashboard");
  };

  // Straight into a sample quiz — no signup, no upload. Seeded by
  // backend/seed_samples.py; ids are stable there.
  const launchSample = (sampleId) => {
    startAnonymousTrial();
    trackEvent("sample_quiz_started", { sample_id: sampleId, source: "landing_hero" });
    navigate(`/quiz/${sampleId}`);
  };

  // The search box sits at the bottom edge of a phone's first screen; take the
  // visitor to it and put the cursor in it.
  const goToTopicSearch = () => {
    trackEvent("hero_topic_search_clicked");
    document.getElementById("topics")?.scrollIntoView({ behavior: "smooth" });
    document.getElementById("topic-query")?.focus({ preventScroll: true });
  };

  const features = [
    {
      icon: BookOpen,
      titleKey: "landing.feature.uploadTitle",
      descKey: "landing.feature.uploadDesc",
    },
    {
      icon: Brain,
      titleKey: "landing.feature.quizTitle",
      descKey: "landing.feature.quizDesc",
    },
    {
      icon: TrendingUp,
      titleKey: "landing.feature.adaptiveTitle",
      descKey: "landing.feature.adaptiveDesc",
    },
    {
      icon: Sparkles,
      titleKey: "landing.feature.mentorTitle",
      descKey: "landing.feature.mentorDesc",
    },
  ];
  const useCases = [
    {
      icon: GraduationCap,
      titleKey: "landing.useCase.satTitle",
      descKey: "landing.useCase.satDesc",
      slug: "sat-act",
    },
    {
      icon: BadgeCheck,
      titleKey: "landing.useCase.certTitle",
      descKey: "landing.useCase.certDesc",
      slug: "professional-certifications",
    },
    {
      icon: School,
      titleKey: "landing.useCase.collegeTitle",
      descKey: "landing.useCase.collegeDesc",
      slug: "college-exams",
    },
    {
      icon: Languages,
      titleKey: "landing.useCase.langTitle",
      descKey: "landing.useCase.langDesc",
      slug: "language-learning",
    },
    {
      icon: Stethoscope,
      titleKey: "landing.useCase.medTitle",
      descKey: "landing.useCase.medDesc",
      slug: "nclex",
    },
    {
      icon: Code,
      titleKey: "landing.useCase.techTitle",
      descKey: "landing.useCase.techDesc",
      slug: "technical-training",
    },
  ];
  const faqItems = [1, 2, 3, 4, 5, 6].map((n) => ({
    question: t(`landing.faq.q${n}`),
    answer: t(`landing.faq.a${n}`),
  }));
  // Factual product capabilities (no fabricated user metrics — the product is new).
  const stats = [
    { value: 3, suffix: "", labelKey: "landing.stats.formats" },
    { value: 5, suffix: "", labelKey: "landing.stats.level" },
    { value: 50, suffix: "k", labelKey: "landing.stats.chars" },
    { display: "∞", labelKey: "landing.stats.questions" },
  ];
  const heroTrialLabel =
    demoBookLimit > 0
      ? t("landing.trialLabel", { count: demoBookLimit })
      : t("landing.trialLabelNoBooks");
  return (
    <div className="min-h-screen bg-app">
      <PageMeta
        title="AI Study Coach — Turn Your Notes & PDFs Into Practice Questions | FlexLearnAI"
        description="Stuck studying? FlexLearnAI is your personal AI study coach. Upload your own notes, PDFs, or textbook and get practice questions and a study plan that meets you at your level."
        canonicalPath="/"
      />
      <JsonLd data={homeGraph(faqItems)} />
      <Header />

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-32 pb-20 px-6 bg-aurora">
        {!reduceMotion && (
          <>
            <motion.div
              aria-hidden="true"
              className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-terracotta/20 blur-3xl"
              animate={{ x: [0, 30, 0], y: [0, 22, 0] }}
              transition={{ duration: 16, repeat: Number.POSITIVE_INFINITY, ease: "easeInOut" }}
            />
            <motion.div
              aria-hidden="true"
              className="pointer-events-none absolute top-8 right-0 h-80 w-80 rounded-full bg-secondary/20 blur-3xl"
              animate={{ x: [0, -26, 0], y: [0, 26, 0] }}
              transition={{ duration: 20, repeat: Number.POSITIVE_INFINITY, ease: "easeInOut" }}
            />
          </>
        )}
        <div className="relative max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-left"
            >
              <h1 className="font-heading font-black text-balance text-4xl sm:text-6xl lg:text-7xl text-foreground leading-[1.04] sm:leading-[1.02] tracking-[-0.03em] sm:tracking-[-0.035em] mb-6">
                {t("landing.heroTitle")}
                <br />
                <span>
                  <span className="text-secondary">Flex</span>
                  <span className="text-terracotta">LearnAI</span>
                </span>
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-[42ch] font-body">
                {t("landing.heroSubtitle")}
              </p>

              <div className="flex w-fit flex-col items-center">
                <div className="flex flex-col sm:flex-row gap-4">
                  {isClerkConfigured ? (
                    <>
                      <SignedIn>
                        <Button
                          onClick={() => navigate("/dashboard")}
                          className="bg-terracotta hover:bg-terracotta-hover text-white rounded-full px-8 py-6 text-lg font-semibold btn-press"
                          data-testid="hero-dashboard-btn"
                        >
                          {t("landing.goToDashboard")}
                          <ArrowRight className="ml-2 w-5 h-5" />
                        </Button>
                      </SignedIn>
                      <SignedOut>
                        <div className="flex flex-col items-center gap-2">
                          <Button
                            onClick={goToTopicSearch}
                            className="bg-terracotta hover:bg-terracotta-hover text-white rounded-full px-8 py-6 text-lg font-semibold btn-press w-full shadow-[0_10px_30px_-10px_rgba(224,82,54,0.6)]"
                            data-testid="hero-topic-btn"
                          >
                            {t("landing.practiceAnyTopic")}
                            <ArrowRight className="ml-2 w-5 h-5" />
                          </Button>
                          <button
                            onClick={() => launchSample(SAMPLE_SAT_ID)}
                            className="text-sm font-medium text-terracotta hover:text-terracotta-hover transition-colors flex items-center gap-1.5 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terracotta/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background px-1 py-0.5"
                            data-testid="hero-sample-sat-btn"
                          >
                            <span className="relative flex h-2 w-2">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-terracotta opacity-75" />
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-terracotta" />
                            </span>
                            {t("landing.trySatSample")}
                          </button>
                          <button
                            onClick={() => launchSample(SAMPLE_NCLEX_ID)}
                            className="text-sm font-medium text-terracotta hover:text-terracotta-hover transition-colors rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terracotta/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background px-1 py-0.5"
                            data-testid="hero-sample-btn"
                          >
                            {t("landing.tryNclexSampleShort")}
                          </button>
                          <button
                            onClick={launchTrial}
                            className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terracotta/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background px-1 py-0.5"
                            data-testid="hero-demo-btn"
                          >
                            {heroTrialLabel}
                          </button>
                        </div>
                      </SignedOut>
                    </>
                  ) : (
                    <Button
                      onClick={launchTrial}
                      className="bg-terracotta hover:bg-terracotta-hover text-white rounded-full px-8 py-6 text-lg font-semibold btn-press"
                      data-testid="hero-demo-btn"
                    >
                      {t("landing.tryFree")}
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  )}
                </div>

                <a
                  href="#topics"
                  className="mt-6 inline-flex items-center gap-1.5 rounded-full px-1 py-0.5 font-body text-sm font-medium text-muted-foreground transition-colors hover:text-terracotta focus-visible:ring-2 focus-visible:ring-terracotta/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none"
                  data-testid="hero-scroll-hint"
                >
                  {t("landing.scrollForTopics")}
                  <ChevronDown className="h-4 w-4 animate-bounce" aria-hidden="true" />
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <HeroIllustration />
              {/* Single deliberate glassmorphism accent — a factual trust chip
                  floating over the illustration edge on the aurora backdrop. */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.7 }}
                className="glass-panel absolute -bottom-5 -left-5 z-20 flex items-center gap-2.5 rounded-2xl px-4 py-3 shadow-warm"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-600">
                  <ShieldCheck className="h-5 w-5" strokeWidth={2.5} />
                </span>
                <span className="font-heading text-sm font-bold text-foreground">
                  {t("landing.heroChip.text")}
                </span>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The no-upload entry point. A visitor arriving from a video has no PDF —
          this is the path that gets them to a question anyway. */}
      <section id="topics" className="relative z-10 scroll-mt-24 px-6 pb-16">
        <div className="mx-auto max-w-3xl">
          <p className="text-terracotta text-center font-body text-sm font-bold tracking-[0.2em] uppercase">
            {t("landing.topicsEyebrow")}
          </p>
          <h2 className="text-foreground font-heading mt-2 text-center text-3xl font-black text-balance sm:text-4xl">
            {t("landing.topicsTitle")}
          </h2>
          <TopicPicker className="mt-8" />
          {/* The picker only mounts a category's links once it's opened, so without this
              the homepage — our most-crawled page — carries no link to any topic. */}
          <p className="mt-6 text-center font-body text-sm text-muted-foreground">
            {t("landing.nursingTopicsLead")}{" "}
            {nursingTopics.map((topic, index) => (
              <span key={topic.slug}>
                {index > 0 ? " · " : null}
                <Link
                  to={`/learn/${topic.slug}`}
                  className="font-semibold text-terracotta hover:underline"
                >
                  {topic.name}
                </Link>
              </span>
            ))}
          </p>
        </div>
      </section>

      {/* Social proof / stats band */}
      <section className="relative z-10 -mt-8 px-6">
        <div className="mx-auto max-w-5xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.4 }}
            onViewportEnter={() => setStatsInView(true)}
            variants={{
              hidden: {},
              visible: { transition: { staggerChildren: 0.08 } },
            }}
            className="grid grid-cols-2 gap-4 rounded-[28px] border border-border/70 bg-card p-6 shadow-warm-lg sm:gap-6 md:grid-cols-4 md:p-8"
          >
            {stats.map((stat) => (
              <motion.div
                key={stat.labelKey}
                variants={{
                  hidden: { opacity: 0, y: 16 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.45 } },
                }}
                className="text-center"
              >
                <div className="font-heading text-4xl font-black tabular-nums text-terracotta sm:text-5xl">
                  {stat.display ? (
                    stat.display
                  ) : (
                    <>
                      <AnimatedNumber to={stat.value} start={statsInView} />
                      {stat.suffix}
                    </>
                  )}
                </div>
                <div className="mt-2 font-body text-sm leading-snug text-muted-foreground">
                  {t(stat.labelKey)}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 px-6 scroll-mt-24">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5 }}
            className="mb-12"
          >
            <div className="mx-auto flex max-w-4xl flex-col items-center gap-4 text-center sm:flex-row sm:items-end sm:justify-start sm:gap-8 sm:text-left">
              <motion.img
                src="/Flexo-neutral.v1.webp"
                alt={t("landing.flexo.mascotAlt")}
                className="h-20 w-auto shrink-0 sm:h-28 lg:h-32"
                width="128"
                height="128"
                loading="lazy"
                decoding="async"
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 2.8, repeat: Number.POSITIVE_INFINITY, ease: "easeInOut" }}
              />
              <div className="max-w-2xl">
                <span className="mb-4 block text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
                  {t("landing.about.eyebrow")}
                </span>
                <h2 className="mb-4 font-heading text-balance text-3xl font-black tracking-[-0.03em] leading-[1.05] text-foreground sm:text-5xl">
                  {t("landing.about.title")}
                </h2>
                <p className="font-body text-lg text-muted-foreground">{t("landing.about.body")}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            ref={carouselRef}
          >
            {showCarousel ? (
              <Suspense
                fallback={<div className="h-[420px] rounded-3xl bg-muted/40 animate-pulse" />}
              >
                <FlexoCarousel />
              </Suspense>
            ) : (
              <div className="h-[420px] rounded-3xl bg-muted/40 animate-pulse" />
            )}
          </motion.div>
        </div>
      </section>

      {/* How It Works (Features) Section */}
      <section id="how-it-works" className="py-24 px-6 scroll-mt-24">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16 max-w-2xl">
            <span className="mb-4 block text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
              {t("landing.howItWorks")}
            </span>
            <h2 className="font-heading font-black text-balance text-3xl sm:text-5xl tracking-[-0.03em] leading-[1.05] text-foreground">
              {t("landing.learningMadeSimple")}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
            {features.map((feature, index) => {
              const variants = [
                {
                  card: "bg-card border border-border/70 rounded-[28px] shadow-warm",
                  chip: "bg-terracotta-light text-terracotta",
                },
                {
                  card: "bg-card border border-border/70 rounded-3xl shadow-warm",
                  chip: "bg-secondary/10 text-secondary",
                },
                {
                  card: "bg-surface-muted border border-border/60 rounded-[32px] shadow-none",
                  chip: "bg-card text-terracotta border border-border/60",
                },
                {
                  card: "bg-card border border-border/70 rounded-3xl shadow-warm",
                  chip: "bg-emerald-500/10 text-emerald-600",
                },
              ];
              const v = variants[index % variants.length];
              return (
                <motion.div
                  key={feature.titleKey}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`p-8 card-hover card-accent-top ${v.card}`}
                  data-testid={`feature-card-${index}`}
                >
                  <div
                    className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 ${v.chip}`}
                  >
                    <feature.icon className="w-7 h-7" strokeWidth={2.5} />
                  </div>
                  <h3 className="font-heading font-bold text-xl text-foreground mb-3 text-balance">
                    {t(feature.titleKey)}
                  </h3>
                  <p className="text-muted-foreground font-body leading-relaxed">
                    {t(feature.descKey)}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section id="use-cases" className="relative py-24 px-6 scroll-mt-24 overflow-hidden">
        <div className="bg-grain pointer-events-none absolute inset-0" aria-hidden="true" />
        <div className="relative max-w-6xl mx-auto">
          <div className="mb-16 grid gap-6 md:grid-cols-[1fr_auto] md:items-end">
            <h2 className="font-heading font-black text-balance text-3xl sm:text-5xl tracking-[-0.03em] leading-[1.05] text-foreground max-w-2xl">
              {t("landing.useCases")}
            </h2>
            <p className="text-lg text-muted-foreground font-body max-w-md md:text-right">
              {t("landing.useCasesSubtitle")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-5 lg:gap-6">
            {useCases.map((useCase, index) => {
              // Zig-zag rows on lg (6 cols): row 1 = 4+2, row 2 = 2+4, row 3 = 4+2.
              // Pattern repeats predictably: indices 0,3,4 wide; 1,2,5 narrow.
              const widePattern = [true, false, false, true, true, false];
              const span = widePattern[index] ? "lg:col-span-4" : "lg:col-span-2";
              const accent =
                index % 3 === 0
                  ? "bg-terracotta-light text-terracotta"
                  : index % 3 === 1
                    ? "bg-secondary/10 text-secondary"
                    : "bg-emerald-500/10 text-emerald-600";
              return (
                <MotionLink
                  key={useCase.titleKey}
                  to={`/study-coach/${useCase.slug}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.45, delay: (index % 3) * 0.08 }}
                  className={`block bg-card border border-border/70 rounded-3xl p-8 shadow-warm card-hover ${span}`}
                >
                  <div
                    className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 ${accent}`}
                  >
                    <useCase.icon className="w-7 h-7" strokeWidth={2.5} />
                  </div>
                  <h3 className="font-heading font-bold text-xl text-foreground mb-3 text-balance">
                    {t(useCase.titleKey)}
                  </h3>
                  <p className="text-muted-foreground font-body leading-relaxed">
                    {t(useCase.descKey)}
                  </p>
                </MotionLink>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-6 scroll-mt-24" ref={pricingRef}>
        {showPricing ? (
          <Suspense
            fallback={
              <div className="mx-auto h-[620px] max-w-6xl rounded-3xl bg-muted/40 animate-pulse" />
            }
          >
            <PricingSection />
          </Suspense>
        ) : (
          <div className="mx-auto h-[620px] max-w-6xl rounded-3xl bg-muted/40 animate-pulse" />
        )}
      </section>

      {/* FAQ Section */}
      <section id="faq" className="relative py-24 px-6 scroll-mt-24">
        <div className="relative max-w-3xl mx-auto">
          <div className="mb-12 text-center">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
              {t("landing.faq.eyebrow")}
            </p>
            <h2 className="mt-3 font-heading font-black text-balance text-3xl sm:text-5xl tracking-[-0.03em] leading-[1.05] text-foreground">
              {t("landing.faq.title")}
            </h2>
          </div>

          <div className="space-y-4">
            {faqItems.map((item) => (
              <div
                key={item.question}
                className="rounded-3xl border border-border/70 bg-card p-6 shadow-warm sm:p-8"
              >
                <h3 className="font-heading font-bold text-lg text-foreground">{item.question}</h3>
                <p className="mt-2 text-muted-foreground font-body leading-relaxed">
                  {item.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="relative overflow-hidden rounded-[32px] p-12 sm:p-16 text-center bg-[#2A1F1A] dark:bg-card dark:border dark:border-border shadow-warm-lg">
            {/* Off-axis warm wash, on-brand instead of pure black */}
            <motion.div
              className="absolute inset-0 pointer-events-none"
              aria-hidden="true"
              style={{
                background:
                  "radial-gradient(80% 60% at 100% 0%, rgba(224,82,54,0.35), transparent 60%), radial-gradient(70% 60% at 0% 100%, rgba(56,189,248,0.18), transparent 65%)",
              }}
              animate={reduceMotion ? undefined : { opacity: [0.85, 1, 0.85], scale: [1, 1.03, 1] }}
              transition={{ duration: 7, repeat: Number.POSITIVE_INFINITY, ease: "easeInOut" }}
            />
            <div
              className="bg-grain pointer-events-none absolute inset-0 opacity-[0.35]"
              aria-hidden="true"
            />
            <div className="relative">
              <h2 className="font-heading font-black text-balance text-3xl sm:text-5xl tracking-[-0.03em] leading-[1.05] text-white mb-5">
                {t("landing.readyToTransform")}
              </h2>
              <p className="text-stone-300 dark:text-muted-foreground text-lg sm:text-xl mb-10 max-w-xl mx-auto font-body">
                {t("landing.joinThousands")}
              </p>
              <Button
                onClick={() => launchSample(SAMPLE_NCLEX_ID)}
                className="bg-terracotta hover:bg-terracotta-hover text-white rounded-full px-10 py-6 text-lg font-semibold btn-press shadow-[0_10px_30px_-10px_rgba(224,82,54,0.6)]"
                data-testid="cta-btn"
              >
                {t("landing.tryNclexSample")}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
